import React, { useState } from 'react';
import {
  AimOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  GiftOutlined,
  GlobalOutlined,
  HourglassOutlined,
  LockOutlined,
  NotificationOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
  TeamOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import { Input } from 'antd';
import styled, { css } from 'styled-components';

import { FEATURES } from 'appConstants';
import { IBuilderSaveResult } from 'components/EmbeddedBuilder';
import { useFeatureFlags } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { PlainButton } from '../components/pageStyles';
import { ConfigHandoff, ConfigSubmit } from '../configStudio/configSubmission';
import { TC } from '../configStudio/kit/ConfigPanel';
import ConfigResultView from '../configStudio/kit/ConfigResultView';
import ConfigWizardRoot from '../configStudio/kit/ConfigWizardRoot';
import { AddButton } from '../wizard/kit/controls';
import { SelectField } from '../wizard/kit/fields';
import { ErrorList } from '../wizard/kit/layout';
import { PrimaryButton, SecondaryButton, tint, wz, wzType } from '../wizard/wizardStyles';
import { audienceErrors, AudienceWizardValues, buildAudienceValues } from './audienceAdapter';
import {
  ATTRIBUTE_GROUPS,
  AUDIENCE_OPERATORS,
  AudienceAttribute,
  AudienceOperator,
  AudienceProperty,
  AudienceState,
  initialAudienceState,
  newProperty,
  toggleAttribute
} from './audienceModel';

const TA = `${TC}.audience`;

const ICONS: Record<AudienceAttribute, React.ReactNode> = {
  age: <HourglassOutlined />,
  birthmonth: <CalendarOutlined />,
  gender: <TeamOutlined />,
  race: <GlobalOutlined />,
  location: <EnvironmentOutlined />,
  earned_badges: <SafetyCertificateOutlined />,
  campaign_enrolment: <NotificationOutlined />,
  leaderboard_position: <TrophyOutlined />,
  loyalty_points_balance: <AimOutlined />,
  loyalty_tier: <StarOutlined />,
  account_confirmed_at: <ClockCircleOutlined />,
  earned_rewards: <GiftOutlined />,
  custom: <FilterOutlined />
};

// 6985:8176 `div.sheet`: 1480 wide, two stacked cards on the panel fill.
const Sheet = styled.div`
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;

  > * + * {
    margin-top: 18px;
  }
`;

const Section = styled.section`
  padding: 26px 24px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 16px;
  background: ${wz.panel};
`;

const Title = styled.h1`
  margin: 0 0 22px;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.title}
`;

const SectionTitle = styled.h2`
  margin: 0 0 22px;
  color: ${wz.text};
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
`;

const NameRow = styled.label`
  display: flex;
  align-items: center;

  > span {
    flex: none;
    margin-right: 16px;
    color: ${wz.secondary};
    ${wzType.body}
  }

  > span > em {
    margin-right: 4px;
    color: ${wz.danger};
    font-style: normal;
  }

  .ant-input-affix-wrapper {
    max-width: 948px;
    height: 44px;
    border: ${wz.line} solid ${wz.border};
    border-radius: 8px;
    background: ${wz.field};
  }

  .ant-input-affix-wrapper .ant-input {
    height: auto;
    padding: 0;
    border: 0;
    background: transparent;
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 692px);
  column-gap: 24px;
`;

const Question = styled.h3`
  margin: 0;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.question}
`;

const Hint = styled.p`
  margin: 4px 0 18px;
  color: ${wz.muted};
  ${wzType.label}
`;

const Group = styled.h4`
  margin: 18px 0 10px;
  color: ${wz.muted};
  font-weight: 800;
  text-transform: uppercase;
  ${wzType.eyebrow}
`;

const Tiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -10px -10px 0;
`;

// 68 x 68 icon tiles with an upper-case label; "Custom" picked draws the accent border.
const Tile = styled(PlainButton)<{ $on: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  margin: 0 10px 10px 0;
  padding: 4px;
  border-radius: 8px;
  color: ${({ $on }) => ($on ? wz.accent : wz.secondary)};
  font-size: 8.5px;
  font-weight: 700;
  line-height: 10px;
  text-transform: uppercase;
  text-align: center;
  ${({ $on }) =>
    $on
      ? css`
          border: 1px solid ${wz.accent};
          background: ${tint(wz.accent, 0.14)};
        `
      : css`
          border: 1px solid ${wz.border};
          background: ${wz.field};
        `}

  > :first-child {
    margin-bottom: 6px;
    font-size: 18px;
  }
`;

const Panel = styled.div`
  padding: 20px 22px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 12px;
  background: ${wz.page};
`;

const PropertyCard = styled.div`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) 32px;
  column-gap: 14px;
  padding: 14px 0 18px;

  & + & {
    border-top: ${wz.line} solid ${wz.border};
  }
`;

const Kind = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 8px;
  color: ${wz.muted};
  font-size: 8.5px;
  font-weight: 700;
  text-transform: uppercase;

  > :first-child {
    margin-bottom: 6px;
    font-size: 16px;
  }
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;

  > .full {
    grid-column: 1 / -1;
  }

  .ant-input {
    width: 100%;
  }
`;

const Lock = styled.span`
  display: inline-flex;
  align-items: center;
  justify-self: start;
  padding: 3px 10px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 999px;
  color: ${wz.secondary};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;

  > * + * {
    margin-left: 6px;
  }
`;

const Caption = styled.span`
  color: ${wz.secondary};
  ${wzType.label}
`;

const Trash = styled(PlainButton)`
  align-self: start;
  margin-top: 8px;
  color: ${wz.muted};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Count = styled.p`
  display: flex;
  align-items: center;
  margin: 16px 0 0;
  color: ${wz.muted};
  ${wzType.label}

  > * + * {
    margin-left: 8px;
  }
`;

// Not drawn: 68 ends at the property card with no way to save; the module form's two buttons.
const Footer = styled.footer`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;

  > * + * {
    margin-left: 12px;
  }
`;

const Muted = styled.p`
  margin: 0;
  color: ${wz.muted};
  ${wzType.hint}
`;

interface IPropertyCardProps {
  property: AudienceProperty;
  onChange: (patch: Partial<AudienceProperty>) => void;
  onRemove: () => void;
}

const PropertyFields: React.FC<IPropertyCardProps> = ({ property, onChange, onRemove }) => {
  const { t } = useMultiLanguageTranslation();
  const { locked } = property;
  return (
    <PropertyCard data-testid="orbit-audience-property" data-locked={locked}>
      <Kind aria-hidden="true">
        <FilterOutlined />
        <span>{t(`${TA}.attributes.custom`)}</span>
      </Kind>
      <Fields>
        <span className="full">
          <SelectField<string>
            label={t(`${TA}.string`)}
            width="xl"
            value="string"
            disabled={locked}
            options={[{ value: 'string', label: t(`${TA}.string`) }]}
            onChange={() => undefined}
          />
        </span>
        {locked ? (
          <Lock className="full" title={t(`${TA}.lockedHint`)}>
            <LockOutlined aria-hidden="true" />
            <span>{t(`${TA}.locked`)}</span>
          </Lock>
        ) : null}
        <Caption className="full">{t(`${TA}.keyValue`)}</Caption>
        <span className="full">
          <Input
            aria-label={t(`${TA}.key`)}
            placeholder={t(`${TA}.key`)}
            value={property.key}
            disabled={locked}
            onChange={event => onChange({ key: event.target.value })}
          />
        </span>
        <SelectField<AudienceOperator>
          label={t(`${TA}.operatorLabel`, { key: property.key || t(`${TA}.key`) })}
          width="lg"
          value={property.operator}
          disabled={locked}
          options={AUDIENCE_OPERATORS.map(operator => ({
            value: operator,
            label: t(`${TA}.operators.${operator}`)
          }))}
          onChange={operator => onChange({ operator })}
        />
        <Input
          aria-label={`${property.key || t(`${TA}.key`)} ${t(`${TA}.value`)}`}
          placeholder={t(`${TA}.value`)}
          value={property.value}
          disabled={locked}
          onChange={event => onChange({ value: event.target.value })}
        />
      </Fields>
      <Trash
        type="button"
        aria-label={t(`${TA}.removeProperty`)}
        disabled={locked}
        onClick={onRemove}
      >
        <DeleteOutlined />
      </Trash>
    </PropertyCard>
  );
};

interface IAudienceBuilderProps {
  onSubmit: ConfigSubmit<AudienceWizardValues>;
  onExit: () => void;
  /** Told about every audience a save created. */
  onHandoff: ConfigHandoff;
  /** Orbit's proposal: a name and the locked Layer 2 property (68). */
  initialState?: AudienceState;
}

interface ISaved {
  name: string;
  result: IBuilderSaveResult;
}

/** The standalone "Create Audience" sheet (68): a name, attribute tiles and custom properties. */
const AudienceBuilder: React.FC<IAudienceBuilderProps> = ({
  onSubmit,
  onExit,
  onHandoff,
  initialState
}) => {
  const { t } = useMultiLanguageTranslation();
  const features = useFeatureFlags() as Record<string, boolean | undefined>;
  const [state, setState] = useState<AudienceState>(() => initialState ?? initialAudienceState());
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<ISaved | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const errors = audienceErrors(state, t);
  // The module drops demographics without its flag (`AudienceFilters.tsx`).
  const groups = ATTRIBUTE_GROUPS.filter(
    group =>
      group.group !== 'demographics' ||
      features[FEATURES.AUDIENCE_DEMOGRAPHICS_SELECTION.key] === true
  );
  const others = state.attributes.filter(attribute => attribute !== 'custom');

  const setProperty = (id: number, patch: Partial<AudienceProperty>) =>
    setState(current => ({
      ...current,
      properties: current.properties.map(property =>
        property.id === id ? { ...property, ...patch } : property
      )
    }));

  const create = async () => {
    setAttempted(true);
    if (errors.length > 0) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // An audience has no draft in the module: creating it is the one intent.
      const result = await onSubmit({ intent: 'confirm', values: buildAudienceValues(state) });
      if (result !== null) {
        setSaved({ name: state.name.trim(), result });
        setState(initialAudienceState());
        setAttempted(false);
        onHandoff(result);
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : '';
      setSaveError(reason !== '' ? reason : t(`${TC}.saveFailed`));
    } finally {
      setSaving(false);
    }
  };

  if (saved !== null) {
    return (
      <ConfigWizardRoot testId="orbit-audience-builder" step="result" onJump={null}>
        <ConfigResultView
          entity={t(`${TA}.entity`)}
          name={saved.name}
          result={saved.result}
          onCreateAnother={() => setSaved(null)}
        />
      </ConfigWizardRoot>
    );
  }

  const custom = state.properties;
  return (
    <ConfigWizardRoot testId="orbit-audience-builder" step="sheet" onJump={null}>
      <Sheet>
        <Section>
          <Title>{t(`${TA}.title`)}</Title>
          <NameRow>
            <span>
              <em aria-hidden="true">*</em>
              {t(`${TA}.name`)}
            </span>
            <Input
              allowClear
              value={state.name}
              onChange={event => {
                const name = event.target.value;
                setState(current => ({ ...current, name }));
              }}
            />
          </NameRow>
        </Section>
        <Section>
          <SectionTitle>{t(`${TA}.list`)}</SectionTitle>
          <Columns>
            <div>
              <Question>{t(`${TA}.question`)}</Question>
              <Hint>{t(`${TA}.hint`)}</Hint>
              {groups.map(group => (
                <div key={group.group}>
                  <Group>{t(`${TA}.groups.${group.group}`)}</Group>
                  <Tiles role="group" aria-label={t(`${TA}.groups.${group.group}`)}>
                    {group.attributes.map(attribute => (
                      <Tile
                        key={attribute}
                        type="button"
                        aria-pressed={state.attributes.includes(attribute)}
                        $on={state.attributes.includes(attribute)}
                        onClick={() => setState(current => toggleAttribute(current, attribute))}
                      >
                        <span aria-hidden="true">{ICONS[attribute]}</span>
                        <span>{t(`${TA}.attributes.${attribute}`)}</span>
                      </Tile>
                    ))}
                  </Tiles>
                </div>
              ))}
            </div>
            <Panel>
              {custom.map(property => (
                <PropertyFields
                  key={property.id}
                  property={property}
                  onChange={patch => setProperty(property.id, patch)}
                  onRemove={() =>
                    setState(current => ({
                      ...current,
                      properties: current.properties.filter(item => item.id !== property.id)
                    }))
                  }
                />
              ))}
              {others.length > 0 ? (
                <Muted>
                  {`${others.map(item => t(`${TA}.attributes.${item}`)).join(', ')}: ${t(
                    `${TA}.attributeUnsupported`
                  )}`}
                </Muted>
              ) : null}
              <AddButton
                label={t(`${TA}.addCustom`)}
                onClick={() =>
                  setState(current => ({
                    ...current,
                    attributes: current.attributes.includes('custom')
                      ? current.attributes
                      : [...current.attributes, 'custom'],
                    properties: [...current.properties, newProperty()]
                  }))
                }
              />
              <Count>
                <TeamOutlined aria-hidden="true" />
                <span>{t(`${TA}.count`, { count: custom.length })}</span>
              </Count>
            </Panel>
          </Columns>
          {(attempted && errors.length > 0) || saveError !== null ? (
            <ErrorList role="alert">
              {(attempted ? errors : []).map(error => (
                <li key={error}>{error}</li>
              ))}
              {saveError !== null ? <li>{saveError}</li> : null}
            </ErrorList>
          ) : null}
          <Footer>
            <SecondaryButton type="button" onClick={onExit}>
              {t(`${TA}.cancel`)}
            </SecondaryButton>
            <PrimaryButton type="button" disabled={saving} onClick={create}>
              {t(`${TA}.create`)}
            </PrimaryButton>
          </Footer>
        </Section>
      </Sheet>
    </ConfigWizardRoot>
  );
};

export default AudienceBuilder;
